import { Modal } from "react-bootstrap";
import apis from "../../../services";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
  setLanguageCode,
  setTranslatedData,
} from "../../../redux/slice/LanguageSlice";

// Language codes mapping
const language_codes = {
  English: "en",
  "Mandarin Chinese": "zh",
  Hindi: "hi",
  Spanish: "es",
  French: "fr",
  "Modern Standard Arabic": "ar",
  Bengali: "bn",
  Portuguese: "pt",
  Russian: "ru",
  Urdu: "ur",
  Indonesian: "id",
  German: "de",
  Japanese: "ja",
  Marathi: "mr",
  Telugu: "te",
  Turkish: "tr",
  Tamil: "ta",
  "Yue Chinese": "yue",
  Vietnamese: "vi",
};

// Field mapping per QR type
const typeFieldMap = {
  business_page: [
    "business_company",
    "business_title",
    "business_subtitle",
    "business_subtitle",
    "business_btn_text",
    "business_name",
    "business_description",
    "business_about",
  ],
  vcard: [
    "vcard_full_name",

    "vcard_address",
    "vcard_company_name",
    "vcard_summary",
    "vcard_profession",
  ],
  social_media: ["media_headline", "media_description"],
  apps: ["app_name", "app_company", "app_description"],
  video: ["video_name", "video_title", "video_description", "video_button"],
  events: [
    "event_title",
    "event_description",
    "event_btn_text",
    "event_time_action_title",
    "event_location_address",
    "event_location_country",
    "event_organizer_name",
    "event_organizer_about",
  ],
  links: ["links_title", "links_description"],
  pdf: ["pdf_company", "pdf_title", "pdf_description"],
  image_gallery: ["gallery_title", "gallery_description", "gallery_btn_text"],
  landing: [
    "landing_company",
    "landing_title",
    "landing_subtitle",
    "landing_btn_text",
  ],
  elabels: {
    wine: [
      "product_name",
      "sku_elables",
      "alcohol_type",
      "custom_type_wine",
      "grape_variety",
      "task_notes",
      "drinking_temperature",
      "stored_temp_unopen",
      "stored_temp_open",
      "description",
    ],
    beer: ["product_name", "description", "brewed", "sku_elables"],
    cigars: [
      "product_name",
      "sku_elables",
      "description",
      "where_it_is_made",
      "size",
      "wrapper",
      "binder",
      "filler",
      "strength",
      "body",
      "flavour_profile",
      "best_paired_with",
    ],
    coffee: [
      "product_name",
      "sku_elables",
      "description",
      "origin",
      "farm",
      "altitude",
      "roast",
      "flavour",
      "task_notes",
      "ingredients",
      "storage",
    ],
    food: ["product_name", "cuisine", "ingredients", "size", "sku_elables"],
    product: [
      "product_name",
      "sku_elables",
      "description",
      "directions",
      "brand",
      "category",
      "price",
      "warning",
    ],
  },
};

//Custom fiels adds on
const customFieldsMap = {
  business_page: {
    openingHour: "Opening hours",
    location: "Location",
    facilities: "Facilities",
    contactInfo: "Contact information",
    aboutCompany: "About Company",
    closed: "Closed",
    name: "Name",
    phone: "Phone",
    email: "Email",
    website: "Website",
  },
  vcard: {
    email: "Email",
    mobilePhone: "Mobile Phone",
    alternatePhone: "Alternative Phone Number",
    fax: "Fax",
    location: "Location",
    website: "Website",
    companyName: "Company Name",
    profession: "Profession",
    addToContact: "Add To Contact",
  },
  social_media: {},
  apps: {},
  video: {},
  events: {
    dateOfEvent: "Date of the event",
    location: "Location",
    organizerInfo: "Organizer information",
    name: "Name",
    phone: "Phone",
    email: "email",
    website: "Website",
    about: "About",
    start: "Start",
    end: "End",
    zipCode: "Zip Code",
  },
  pdf: {
    seePdfBtn: "seePdfBtn",
  },
  image_gallery: {},
  landing: {},

  // Elabels
  elabels: {
    wine: {
      skuKey: "SKU",
      alcoholType: "Alcohol Type",
      descriptionKey: "Description",
      typeOfWine: "Type of Wine",
      alcoholPercentage: "Alcohol Percentage",
      grapeVariety: "Grape Variety",
      tastingNote: "Tasting Notes",
      yearTitle: "Year",
      drinkingTemperature: "Drinking Temperature",
      storeTempUnopen: "Stored Temp (Unopened)",
      storeTempOpen: "Stored Temp (Opened)",
      dayStoreOpen: "Days Stored Open",
      websiteElabels: "Website",
      // descriptionTitle: "Description",
      descriptionKey: "Description",
    },
    beer: {
      skuKey: "SKU",
      descriptionKey: "Description",
      alcoholPercentage: "Alcohol Percentage",
      ipaKey: "IPA",
      brewedAt: "Brewed At",
      websiteElabels: "Website",
      descriptionKey: "Description",
    },
    cigars: {
      skuKey: "SKU",
      whereItMade: "Where It's Made",
      sizeKey: "Size",
      wrapperKey: "Wrapper",
      binderKey: "Binder",
      fillerKey: "Filler",
      strengthKey: "Strength",
      bodyKey: "Body",
      flavourProfileKey: "Flavour Profile",
      bestPairedKey: "Best Paired With",
      websiteElabels: "Website",
      descriptionKey: "Description",
    },
    coffee: {
      skuKey: "SKU",
      originKey: "Origin",
      farmKey: "Farm",
      altitudeKey: "Altitude",
      roastKey: "Roast",
      flavourKey: "Flavour",
      taskNoteKey: "Task Notes",
      ingredientsKey: "Ingredients",
      websiteElabels: "Website",
      storageKey: "Storage",
      organicKey: "Organic",
      fareTradeKey: "Fare Trade",
      descriptionKey: "Description",
    },
    food: {
      skuKey: "SKU",
      cuisineKey: "Cuisine",
      ingredientsKey: "Ingredients",
      servingSizeKey: "Serving Size",
      caloriesKey: "Calories",
      websiteElabels: "Website",
      descriptionKey: "Description",
    },
    product: {
      skuKey: "SKU",
      directionKey: "Directions",
      brandKey: "Brand",
      categoryKey: "Category",
      priceKey: "Price",
      warningKey: "Warning",
      // descriptionKey:"Description"
      descriptionKey: "Description",
    },
  },
};

const buildTranslationPayload = (language, type, data) => {
  // console.log("datadataQuestionss", data?.questions);
  let fields = [];
  let customFields = {};
  console.log("testtData", data);
  const products = ["wine", "beer", "cigars", "coffee", "food", "product"];
  if (type === "elabels") {
    // Detect the selected product
    const selectedProduct = products.find(
      (product) => data[product] === "true"
    );
    console.log("selectedProductselectedProduct", selectedProduct);

    if (selectedProduct && typeFieldMap.elabels[selectedProduct]) {
      fields = typeFieldMap.elabels[selectedProduct];
    }

    // You can optionally define customFieldsMap.elabels if needed
    customFields = customFieldsMap.elabels?.[selectedProduct] || {};
  } else {
    fields = typeFieldMap[type] || [];
    customFields = customFieldsMap[type] || {};
  }

  // Build text
  const textFromFields = fields.reduce((acc, field) => {
    acc[field] = data[field] ?? "";
    return acc;
  }, {});

  // FOR QUESTION ADD IN PAYLOAD
  const textFromQuestions = (data?.questions || []).reduce(
    (acc, question, index) => {
      const questionKey = `question_${index + 1}_text`;

      if (question.text) {
        acc[questionKey] = question.text;
      }

      if (Array.isArray(question.options)) {
        question.options.forEach((option, optIndex) => {
          acc[`question_${index + 1}_option_${optIndex}`] = option;
        });
      }

      return acc;
    },
    {}
  );

  console.log("textFromQuestionstextFromQuestions", textFromQuestions);

  const text = {
    ...textFromFields,
    ...customFields,
    ...textFromQuestions,
  };

  return {
    target_language: language_codes[language],
    text,
  };
};

const languages = Object.keys(language_codes);

const ChangeLanguageModal = ({
  ShowChangeLanguage,
  setShowChangeLanguage,
  localQrData,
}) => {
  // console.log("localQrDatalocalQrDatamnm", localQrData);
  const dispatch = useDispatch();
  const handleLanguageClick = (language) => {
    const langCode = language_codes[language];
    if (!localQrData?.type) {
      toast.error("QR type is not defined.");
      return;
    }

    const payload = buildTranslationPayload(
      language,
      localQrData.type,
      localQrData
    );

    if (Object.keys(payload.text).length === 0) {
      // toast.warn("No translatable content found for this type.");
      return;
    }
    // console.log("payloadpayload", payload);

    // Set selected language code in Redux
    dispatch(setLanguageCode(langCode));
    mutateChangeLanguage(payload);
  };

  //API CALL FOR CHANGE LANGUAGE
  const { mutate: mutateChangeLanguage, isPending: isLoading } = useMutation({
    mutationFn: apis.ChangeLanguage,
    onError: function (error) {
      toast.error("An error occurred while changing the language.");
      console.log("errorerrorerror", error);
    },
    onSuccess: ({ data, status }) => {
      // toast.success("Language changed successfully.");
      console.log("data?.translated_payload", data?.translated_payload);
      dispatch(setTranslatedData(data?.translated_payload));
      setShowChangeLanguage(false);
    },
  });

  return (
    <Modal
      show={ShowChangeLanguage}
      onHide={() => {
        if (!isLoading) setShowChangeLanguage(false);
      }}
      backdrop={isLoading ? "static" : true}
      keyboard={!isLoading}
      centered
      size="lg"
      className="ChangeLanguage"
    >
      <Modal.Header closeButton>
        <Modal.Title>🌐 Select Your Language</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <div
              className="spinner-border "
              role="status"
              style={{ color: "#e0201c" }}
            ></div>
            <p className="mt-2">Translating...</p>
          </div>
        ) : (
          <div className="language-grid">
            {languages.map((lang, index) => (
              <div
                key={index}
                className="language-card"
                onClick={() => handleLanguageClick(lang)}
              >
                {lang}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChangeLanguageModal;

// Function to build the payload dynamically
// const buildTranslationPayload = (language, type, data) => {
//   console.log("typetype==", type);
//   const fields = typeFieldMap[type] || [];
//   const customFields = customFieldsMap[type] || {};

//   textFromFields = fields.reduce((acc, field) => {
//     acc[field] = data[field] ?? "";
//     return acc;
//   }, {});

//   const text = {
//     ...textFromFields,
//     ...customFields,
//   };
//   return {
//     target_language: language_codes[language],
//     text,
//   };
// };
